import { Fragment } from 'react';
import { useReduxState } from 'hooks';
import { EditorBlockTranslationReference } from 'components/editor';
import { Button, RichTextValue } from 'components/common';
import { FormGroup } from 'components/common/form';
import { CheckboxInput } from 'components/common/form/common';
import {
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';
import { EditorBlockLikertQuestion, EditorBlockLikertOptions } from '.';

import type { BlockLikert } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

export type BlockLikertStateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockLikert = ({
  blockId,
  data,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockLikert> & BlockLikertStateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, serverConfig, theme } = useReduxState();

  const handleDescriptionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            description: html
          }
        }
      }
    });
  };

  const handleFeedbackChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            feedback: html
          }
        }
      }
    });
  };

  const handleQuestionChange = (newText: string, questionIndex: number) => {
    const currentRef = data.questions[questionIndex].question;
    const match = currentRef.match(
      /{{\s*locales\.translatable\.([^}\s]+)\s*}}/
    );
    const localeKey = match ? match[1] : null;
    if (localeKey) {
      data.locales[languageVersion].translatable[localeKey] = newText;
    }
    updateData({ ...data });
  };

  const handleShowResponseRatesChange = (ev) => {
    updateData({ ...data, showResponseRatesAfterSubmit: ev.target.checked });
  };

  const handleShowResponseRatesAllowBeforeChange = (ev) => {
    updateData({
      ...data,
      showResponseRatesAllowBeforeAnswer: ev.target.checked
    });
  };

  const handleShowUserAnswersAverageChange = (ev) => {
    updateData({ ...data, showUserAnswersAverage: ev.target.checked });
  };

  const insertQuestion = () => {
    const newLocaleKey = `question_${data.questions.length + 1}` as const;
    const newQuestionRef =
      `{{ locales.translatable.${newLocaleKey} }}` as const;

    const newQuestions = [
      ...data.questions,
      { id: Date.now(), question: newQuestionRef }
    ];

    const newData: BlockLikert = {
      ...data,
      questions: newQuestions,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [newLocaleKey]: ''
          }
        }
      }
    };
    updateData(newData);

    setTimeout(() => {
      const questionContentEditable = document
        .getElementById(
          `editor-block-likert-question-${blockId}-${newQuestions.length - 1}`
        )
        ?.querySelector('div[contenteditable="true"]') as HTMLInputElement;
      if (questionContentEditable) questionContentEditable.focus();
    });
  };

  const handleQuestionDelete = (index: number) => {
    // save the original count before deleting question
    const oldQuestionCount = data.questions.length;

    // create a new questions array without the question that we are deleting
    const newQuestions = [...data.questions];
    newQuestions.splice(index, 1);

    const translatable = { ...data.locales[languageVersion].translatable };

    // delete the translatable key for the question being removed
    delete translatable[`question_${index + 1}`];

    // shift keys for options that follow
    for (let j = index + 1; j < oldQuestionCount; j++) {
      translatable[`question_${j}`] = translatable[`question_${j + 1}`];
    }

    // clean up the unused keys
    delete translatable[`question_${oldQuestionCount}`];

    // update the placeholders in the questions array
    for (let i = 0; i < newQuestions.length; i++) {
      newQuestions[i] = {
        ...newQuestions[i],
        question: `{{ locales.translatable.question_${i + 1} }}`
      };
    }

    const newData = {
      ...data,
      questions: newQuestions,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: translatable
        }
      }
    };

    updateData(newData);
  };

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="likert">
      <FormGroup maxWidth="100%">
        <div className="block-label">
          {messages.blocksLikert}

          {data.isMandatory ? (
            <span className="block-label-mandatory">
              <span className="block-label-divider">{'•'}</span>
              <span>{messages.commonMandatory}</span>
            </span>
          ) : null}
        </div>

        {isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.description || ''}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable
                  .description || ''
              }
              style={{ marginTop: 5, marginBottom: 5 }}
              translationHtml={
                data.locales[languageVersion].translatable.description
              }
            >
              <TextEditor
                ariaLabel={messages.commonQuestion}
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.description}
                onChange={handleDescriptionChange}
                padding="0"
                placeholder={messages.commonAddTaskDescription}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <div className="options">
        <EditorBlockLikertOptions
          blockId={blockId}
          data={data}
          isPreview={isPreview}
          languageVersion={languageVersion}
          updateData={updateData}
          {...stateProps}
        />
      </div>

      <FormGroup
        label={messages.editorBlockLikertStatements}
        style={{ marginBottom: 10 }}
        maxWidth={'100%'}
      >
        {data.questions.map((questionObj, index) => {
          const match = questionObj.question.match(
            /{{\s*locales\.translatable\.([^}\s]+)\s*}}/
          );
          const localeKey = match ? match[1] : '';
          const questionText =
            data.locales[languageVersion].translatable[localeKey];
          return (
            <Fragment key={questionObj.id}>
              <EditorBlockTranslationReference
                backgroundColor={theme.lightGrey}
                isInnerElement={true}
                key={editorLanguageReference}
                referenceHtml={
                  data.locales[editorLanguageReference]?.translatable[
                    localeKey
                  ] || ''
                }
                style={{ marginTop: index === 0 ? 4 : 12, marginBottom: 5 }}
                translationHtml={questionText}
              >
                <EditorBlockLikertQuestion
                  key={index}
                  blockId={blockId}
                  deleteIsDisabled={
                    data.questions.length === 1 || isEditingLanguageVersion
                  }
                  index={index}
                  isPreview={isPreview}
                  onChange={handleQuestionChange}
                  onDelete={handleQuestionDelete}
                  question={questionText}
                />
              </EditorBlockTranslationReference>
            </Fragment>
          );
        })}
      </FormGroup>

      {!isPreview && (
        <div className="add-question">
          <Button
            category="light"
            disabled={isEditingLanguageVersion}
            icon="plus"
            onClick={insertQuestion}
            size="medium"
            text={messages.editorBlockLikertStatementAdd}
          />
        </div>
      )}

      <FormGroup
        label={messages.editorBlockQuestionFeedbackText}
        maxWidth="100%"
      >
        {isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.feedback || '-'}
            style={{
              backgroundColor: 'white',
              border: `1px solid ${theme.lightGreyDark}`,
              borderRadius: theme.inputBorderRadius,
              color: theme.textColorLight,
              padding: 10
            }}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable.feedback ||
                ''
              }
              style={{ marginTop: 3, marginBottom: 5 }}
              translationHtml={
                data.locales[languageVersion].translatable.feedback
              }
            >
              <TextEditor
                content={data.locales[languageVersion].translatable.feedback}
                onChange={handleFeedbackChange}
                placeholder={messages.commonFeedbackPlaceholder}
                showBorder={true}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      {!isEditingLanguageVersion ? (
        <EditorBlockMoreSettings
          settingsHaveBeenEdited={
            !isPreview &&
            Boolean(
              data?.isMandatory ||
                (serverConfig.likertEnableResponseRates &&
                  (data?.showResponseRatesAfterSubmit ||
                    data?.showResponseRatesAllowBeforeAnswer)) ||
                (serverConfig.likertEnableUserAnswersAverage &&
                  data?.showUserAnswersAverage)
            )
          }
        >
          <EditorBlockIsMandatorySettings
            blockId={blockId}
            data={data}
            disabled={isPreview}
            updateData={updateData}
          />

          {serverConfig.likertEnableResponseRates ? (
            <FormGroup
              label={messages.editorBlockCommonResponseData}
              maxWidth="100%"
            >
              <CheckboxInput
                checked={data.showResponseRatesAfterSubmit || false}
                disabled={isPreview}
                id={`show-response-rates-after-submit-${blockId}`}
                label={messages.editorBlockCommonResponseDataShow}
                onChange={handleShowResponseRatesChange}
              />

              <CheckboxInput
                checked={data.showResponseRatesAllowBeforeAnswer || false}
                disabled={isPreview}
                id={`show-response-rates-allow-before-answer-${blockId}`}
                label={messages.editorBlockCommonResponseDataAllowBefore}
                onChange={handleShowResponseRatesAllowBeforeChange}
                style={{ marginTop: 5 }}
              />
            </FormGroup>
          ) : null}

          {serverConfig.likertEnableUserAnswersAverage ? (
            <FormGroup
              label={messages.editorBlockLikertUserAnswersAverage}
              maxWidth="100%"
            >
              <CheckboxInput
                checked={data.showUserAnswersAverage || false}
                disabled={isPreview}
                id={`show-user-answers-average-${blockId}`}
                label={messages.editorBlockLikertUserAnswersAverageShow}
                onChange={handleShowUserAnswersAverageChange}
              />
            </FormGroup>
          ) : null}
        </EditorBlockMoreSettings>
      ) : null}

      <EditorBlockRequireAnswerToProceedInfo data={data} />

      <style jsx>{`
        .likert {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          counter-reset: likert-questions;
        }
        .block-label {
          color: ${theme.textColorLight};
          display: block;
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 10px;
        }
        .block-label-mandatory {
          color: ${theme.textColorLighter};
          font-weight: 400;
        }
        .block-label-divider {
          margin: 0 5px;
        }
        .options {
          counter-reset: likert-options;
          display: flex;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockLikert;
